import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { MonthPicker } from '../../components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Edit, Trash2, Tag, Download, ChevronDown, ChevronRight, SlidersHorizontal, ImagePlus, X, Loader2, Search, Flag, FileQuestion, FolderX, AlertCircle, CheckCircle2, XCircle, User, Check, FolderTree, ArrowLeft, FolderOpen, Mic, ChevronsUpDown, History, Calendar, BookOpen, ClipboardPen, Eye, Maximize2, Minimize2, MapPin, Layers, AlertTriangle, MoreHorizontal } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import Pagination from '../../components/common/Pagination';
import ImagePickerModal from '../../components/common/ImagePickerModal';
import PictureManagerUploadDialog from '../../components/common/PictureManagerUploadDialog';
import ResearchPanel from '../../components/common/ResearchPanel';
import ExportModal from '../../components/ExportModal';
import BulkAnswerControls from '../../components/admin/BulkAnswerControls';
import TopicPicker from '../../components/shared/TopicPicker';
import TopicPickerMulti from '../../components/shared/TopicPickerMulti';
import SittingsEditor from '../../components/admin/SittingsEditor';
import ReviewChip from '../../components/admin/ReviewChip';
import ReviewBadge from '../../components/admin/ReviewBadge';
import QuestionViewModal from '../../components/admin/QuestionViewModal';
import useReviewConfig from '../../hooks/useReviewConfig';
import { cn } from '../../lib/utils';
import { useMMDLocations } from '../../hooks/useMMDLocations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const grades = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

const REPORT_TYPE_LABELS = {
  incorrect_answer: 'Incorrect Answer',
  incorrect_question: 'Incorrect Question',
  outdated_information: 'Outdated Information',
  duplicate_question: 'Duplicate Question',
  other: 'Other'
};

const AdminQuestionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Role-based access control
  const isFullAdmin = user?.role === 'admin';
  const isSubAdmin = user?.role === 'sub_admin';

  // Iter 113: Needs Review workflow — categories + effective enable check
  const { enabled: reviewEnabled, categories: reviewCategories, getCategory: getReviewCategory, reload: reloadReview } = useReviewConfig();
  // Filter state — 'any' | 'true' | 'false' + category id ('all' | 'uncategorized' | <id>)
  const [reviewFilter, setReviewFilter] = useState('any');
  const [reviewCategoryFilter, setReviewCategoryFilter] = useState('all');
  
  // Helper function to convert month input (YYYY-MM) to text format (Month YYYY)
  const convertMonthInputToText = (monthInput) => {
    if (!monthInput) return '';
    const [year, month] = monthInput.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = parseInt(month) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  };
  
  // Parse a stored `exam_month` string (e.g. "January 2026", "Jan 2026",
  // "jul 2021", " July  2021 ") into the YYYY-MM value the <MonthPicker>
  // expects. Tolerant of:
  //   • Full and abbreviated month names ("January" / "Jan")
  //   • Any letter case ("jul" / "JUL" / "Jul")
  //   • Multiple / leading / trailing whitespace
  // Returns '' for anything it genuinely can't parse — the picker then
  // shows its placeholder and the raw string still appears in the
  // "row-level exam_month display" line below the row.
  //
  // This tolerance matters for legacy prod rows that came in via old
  // `.xlsx` bulk imports, which serialized months in short form ("Jul
  // 2021") through pandas. Without this, the picker showed empty even
  // though the underlying value was correct.
  const convertTextToMonthInput = (textMonth) => {
    if (!textMonth) return '';
    const parts = String(textMonth).trim().split(/\s+/);
    if (parts.length !== 2) return '';
    const [rawMonth, rawYear] = parts;
    if (!/^\d{4}$/.test(rawYear)) return '';
    const key = rawMonth.toLowerCase().slice(0, 3);
    const shortToNum = {
      jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
      jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
    };
    const monthNum = shortToNum[key];
    if (!monthNum) return '';
    return `${rawYear}-${monthNum}`;
  };
  
  // Fetch MMD locations from API
  const { locations: mmdLocations } = useMMDLocations();
  
  const [questions, setQuestions] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedFunction, setSelectedFunction] = useState('all');
  const [selectedMMD, setSelectedMMD] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [selectedInternalSurveyor, setSelectedInternalSurveyor] = useState('all');
  const [selectedExternalSurveyor, setSelectedExternalSurveyor] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateRangeType, setDateRangeType] = useState('none');
  const [filterByAdmin, setFilterByAdmin] = useState('none');
  const [adminIndos, setAdminIndos] = useState('');
  const [sortBy, setSortBy] = useState('approved');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [editTab, setEditTab] = useState('content');
  const [answerExpanded, setAnswerExpanded] = useState(false);
  const [showOriginalQuestion, setShowOriginalQuestion] = useState(false);
  const [showOriginalAnswer, setShowOriginalAnswer] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [topicSearchQuery, setTopicSearchQuery] = useState('');

  // ─── Bulk-topic-edit mode (full-admins only) ──────────────────────────
  // Enabled explicitly via the header toggle. Requires grade + function
  // filters to both be set so we can enforce single-scope bulk edits
  // (topics are function-scoped). Selection is sticky by question_id
  // — filter/search changes don't clear it. See the docstring on
  // /admin/questions/bulk-edit-topics for the server-side belt-&-braces.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());
  const [bulkTopicIds, setBulkTopicIds] = useState([]);
  const [bulkTopicMode, setBulkTopicMode] = useState(
    () => (typeof localStorage !== 'undefined' && localStorage.getItem('oral-bulk-topic-mode-v1')) || 'add'
  );
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPopoverOpen, setBulkPopoverOpen] = useState(false);
  const [bulkReplaceConfirmed, setBulkReplaceConfirmed] = useState(false);
  const canEnableBulk = isFullAdmin && selectedGrade !== 'all' && selectedFunction !== 'all';
  // Actual app-header height in pixels (Layout header + breadcrumb bar +
  // padding). We can't hard-code `top-16` because the header height
  // changes with breadcrumb depth and viewport. Measure the DOM node
  // once on mount + on resize, and stick the bulk bar to that value so
  // it sits *precisely* below the header edge, no overlap and no gap.
  const [headerH, setHeaderH] = useState(72);
  useEffect(() => {
    const measure = () => {
      const el = document.getElementById('app-header');
      if (el) setHeaderH(el.getBoundingClientRect().height);
    };
    measure();
    window.addEventListener('resize', measure);
    // Also re-measure a moment later — first paint sometimes lands
    // before web fonts / breadcrumb crumbs finish laying out.
    const t = setTimeout(measure, 300);
    return () => {
      window.removeEventListener('resize', measure);
      clearTimeout(t);
    };
  }, []);
  // If the admin drops grade or function while bulk is on, auto-off so
  // the invariant "selection ⊆ one scope" can never be violated.
  useEffect(() => {
    if (bulkMode && !canEnableBulk) {
      setBulkMode(false);
      setBulkSelectedIds(new Set());
      toast.info('Bulk mode disabled — Grade + Function filter is required.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEnableBulk, bulkMode]);
  // Sittings editor state: parallel array of asked_by_history entries.
  // Backend recomputes `asked` total + latest surveyor/exam_month from
  // this on save via services.sitting_aggregator.
  const [editingSittings, setEditingSittings] = useState([]);
  const [approvedByIndos, setApprovedByIndos] = useState(null);
  const [lastModifiedByIndos, setLastModifiedByIndos] = useState(null);
  const [editFormData, setEditFormData] = useState({
    question_text: '',
    answer: '',
    answer_images: [],
    topic_ids: [],
    asked_by_internal: false,
    surveyor_internal_id: null,
    surveyor_external_id: null,
    exam_month: '',
    exam_month_input: '',
    has_answer: false,
    is_decider: false,
    is_marq_recommended: false,
    video_url: ''
  });
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [pmUploadOpen, setPmUploadOpen] = useState(false);
  
  // Report-related states
  const [questionReports, setQuestionReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [resolvingReport, setResolvingReport] = useState(null);
  const [expandedReportIds, setExpandedReportIds] = useState(new Set()); // Track which individual reports are expanded

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  
  // Export dialog states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Collapsible filter sections
  const [showDateAdminFilters, setShowDateAdminFilters] = useState(false);
  const [showSortFilters, setShowSortFilters] = useState(false);
  const [showReportsSection, setShowReportsSection] = useState(false); // Will be set based on active reports
  
  // Searchable surveyor combobox states
  const [internalSurveyorComboOpen, setInternalSurveyorComboOpen] = useState(false);
  const [externalSurveyorComboOpen, setExternalSurveyorComboOpen] = useState(false);
  
  // Quick filters
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showUnansweredOnly, setShowUnansweredOnly] = useState(false);
  const [showNoTopicOnly, setShowNoTopicOnly] = useState(false);
  
  // History modal state for showing asked_by_history
  const [historyModal, setHistoryModal] = useState({ open: false, question: null });
  const [showResearch, setShowResearch] = useState(false);
  
  // Quick filter stats (counts)
  const [quickFilterStats, setQuickFilterStats] = useState({
    flagged: 0,
    unanswered: 0,
    no_topic: 0
  });
  
  const [exportFilters, setExportFilters] = useState({
    grade: 'all',
    mmd_location: 'all',
    function_id: 'all',
    topic_id: 'all',
    surveyor_internal_id: 'all',
    surveyor_external_id: 'all',
    exam_month: '',
    decider_only: false
  });
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportCount, setExportCount] = useState(0);

  // Initialize flagged filter from URL params
  useEffect(() => {
    const flaggedParam = searchParams.get('flagged');
    if (flaggedParam === 'true') {
      setShowFlaggedOnly(true);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchFunctions();
    fetchTopics();
    fetchSurveyors();
    fetchQuickFilterStats();
    fetchQuestions();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [selectedGrade, selectedFunction, selectedMMD, selectedTopic, selectedInternalSurveyor, selectedExternalSurveyor, dateFrom, dateTo, dateRangeType, filterByAdmin, adminIndos, searchQuery, sortBy, sortOrder, currentPage, itemsPerPage, showFlaggedOnly, showUnansweredOnly, showNoTopicOnly]);

  // Update quick filter stats when filters change
  useEffect(() => {
    fetchQuickFilterStats();
  }, [selectedGrade, selectedFunction, selectedMMD, selectedTopic, selectedInternalSurveyor, selectedExternalSurveyor]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedGrade, selectedFunction, selectedMMD, selectedTopic, selectedInternalSurveyor, selectedExternalSurveyor, dateFrom, dateTo, dateRangeType, filterByAdmin, adminIndos, searchQuery, sortBy, sortOrder, showUnansweredOnly, showNoTopicOnly]);

  // Fetch surveyors when MMD changes
  // Refetch surveyors when MMD or Grade changes
  useEffect(() => {
    fetchSurveyors();
  }, [selectedMMD, selectedGrade]);

  const fetchFunctions = async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      setFunctions(response.data);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/topics`);
      setTopics(response.data);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  };

  const fetchSurveyors = async () => {
    try {
      const params = {};
      // Filter by MMD if selected
      if (selectedMMD && selectedMMD !== 'all') {
        params.mmd_location = selectedMMD;
      }
      // Filter by grade if selected
      if (selectedGrade && selectedGrade !== 'all') {
        params.grade = selectedGrade;
      }
      const response = await axios.get(`${API}/surveyors`, { params });
      setSurveyors(response.data);
    } catch (error) {
      console.error('Failed to fetch surveyors:', error);
    }
  };

  const fetchQuickFilterStats = async () => {
    try {
      // Build params based on current filters
      const params = {};
      if (selectedGrade && selectedGrade !== 'all') params.grade = selectedGrade;
      if (selectedFunction !== 'all') params.function_id = selectedFunction;
      if (selectedMMD !== 'all') params.mmd_location = selectedMMD;
      if (selectedTopic !== 'all') params.topic_id = selectedTopic;
      if (selectedInternalSurveyor !== 'all') params.surveyor_internal_id = selectedInternalSurveyor;
      if (selectedExternalSurveyor !== 'all') params.surveyor_external_id = selectedExternalSurveyor;
      
      const response = await axios.get(`${API}/admin/questions/quick-filter-stats`, { params });
      setQuickFilterStats(response.data);
    } catch (error) {
      console.error('Failed to fetch quick filter stats:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params = { question_status: 'approved' }; // Only show approved questions
      if (selectedGrade && selectedGrade !== 'all') params.grade = selectedGrade;
      if (selectedFunction !== 'all') params.function_id = selectedFunction;
      if (selectedMMD !== 'all') params.mmd_location = selectedMMD;
      if (selectedTopic !== 'all') params.topic_id = selectedTopic;
      if (selectedInternalSurveyor !== 'all') params.surveyor_internal_id = selectedInternalSurveyor;
      if (selectedExternalSurveyor !== 'all') params.surveyor_external_id = selectedExternalSurveyor;
      
      // Date range filtering
      if (dateRangeType !== 'none' && dateFrom) {
        params.date_range_type = dateRangeType;
        params.date_from = dateFrom;
        if (dateTo) {
          params.date_to = dateTo;
        }
        // If dateTo is empty, backend will use current date
      }
      
      // Admin filter
      if (filterByAdmin && filterByAdmin !== 'none' && adminIndos) {
        params.filter_by_admin = filterByAdmin;
        params.admin_indos = adminIndos;
      }
      
      // Search
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      // Quick filters
      if (showFlaggedOnly) {
        params.flagged_only = true;
      }
      if (showUnansweredOnly) {
        params.unanswered_only = true;
      }
      if (showNoTopicOnly) {
        params.no_topic_only = true;
      }
      
      // Sorting
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      
      // Pagination
      params.page = currentPage;
      params.per_page = itemsPerPage;

      const response = await axios.get(`${API}/admin/questions/all`, { params });
      setQuestions(response.data.items);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    // Only admins can delete questions
    if (!isFullAdmin) {
      toast.error('Admin rights required to delete questions');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await axios.delete(`${API}/questions/${questionId}`);
      toast.success('Question deleted successfully');
      fetchQuestions();
    } catch (error) {
      console.error('Failed to delete question:', error);
      toast.error('Failed to delete question');
    }
  };

  const handleEditQuestion = async (question) => {
    setEditTab('content');
    setAnswerExpanded(false);
    setShowOriginalQuestion(false);
    setShowOriginalAnswer(false);
    setEditingQuestion(question);
    // Handle both legacy topic_id and new topic_ids
    const topicIds = question.topic_ids?.length > 0 
      ? question.topic_ids 
      : (question.topic_id ? [question.topic_id] : []);
    setSelectedTopicIds(topicIds);
    setTopicSearchQuery('');
    // Hydrate the sittings editor. If asked_by_history is already
    // populated (typical for merged questions), use it verbatim. If it's
    // empty, seed one row from the question's top-level surveyor fields
    // so admins see a sensible starting point instead of an empty list.
    const rawHistory = question.asked_by_history || [];
    let seededHistory;
    if (rawHistory.length > 0) {
      seededHistory = rawHistory.map((e) => ({ ...e }));
    } else {
      const isInternal = !!question.asked_by_internal;
      const surveyorId = isInternal
        ? question.surveyor_internal_id
        : question.surveyor_external_id;
      const surveyor = surveyors.find((s) => s.id === surveyorId);
      seededHistory = surveyorId
        ? [{
            surveyor_id: surveyorId,
            surveyor_type: isInternal ? 'internal' : 'external',
            surveyor_name: surveyor?.name || '',
            exam_month: question.exam_month || '',
            mmd_location: question.mmd_location || null,
            asked: question.asked || 1,
            added_at: question.created_at || new Date().toISOString(),
          }]
        : [];
    }
    setEditingSittings(seededHistory);
    setEditFormData({
      question_text: question.question_text || '',
      answer: question.answer_text || '',
      answer_images: question.answer_images || [],
      topic_ids: topicIds,
      asked: question.asked || 1,
      asked_by_internal: question.asked_by_internal || false,
      surveyor_internal_id: question.surveyor_internal_id || null,
      surveyor_external_id: question.surveyor_external_id || null,
      exam_month: question.exam_month || '',
      exam_month_input: convertTextToMonthInput(question.exam_month || ''),
      has_answer: question.has_answer || false,
      is_decider: question.is_decider || false,
      is_marq_recommended: question.is_marq_recommended || false,
      video_url: question.video_url || ''
    });
    
    // Fetch reports for this question
    setLoadingReports(true);
    try {
      const reportsResponse = await axios.get(`${API}/reports/admin/question/${question.id}`);
      const reports = reportsResponse.data.reports || [];
      setQuestionReports(reports);
      
      // Determine which reports are active (pending or under_review)
      const activeReportIds = reports
        .filter(r => r.status === 'pending' || r.status === 'under_review')
        .map(r => r.id);
      
      // If there are active reports, expand the section and those reports
      if (activeReportIds.length > 0) {
        setShowReportsSection(true);
        setExpandedReportIds(new Set(activeReportIds));
      } else {
        // No active reports - collapse everything
        setShowReportsSection(false);
        setExpandedReportIds(new Set());
      }
    } catch (error) {
      console.error('Failed to fetch question reports:', error);
      setQuestionReports([]);
      setShowReportsSection(false);
      setExpandedReportIds(new Set());
    } finally {
      setLoadingReports(false);
    }
    
    // Fetch approved by admin's INDoS number
    if (question.approved_by_admin_id) {
      try {
        const response = await axios.get(`${API}/users/${question.approved_by_admin_id}/indos`);
        setApprovedByIndos(response.data.indos_number);
      } catch (error) {
        console.error('Failed to fetch approved by admin INDoS:', error);
        setApprovedByIndos(null);
      }
    } else {
      setApprovedByIndos(null);
    }
    
    // Fetch last modified by admin's INDoS number
    if (question.last_modified_by_admin_id) {
      try {
        const response = await axios.get(`${API}/users/${question.last_modified_by_admin_id}/indos`);
        setLastModifiedByIndos(response.data.indos_number);
      } catch (error) {
        console.error('Failed to fetch last modified by admin INDoS:', error);
        setLastModifiedByIndos(null);
      }
    } else {
      setLastModifiedByIndos(null);
    }
  };

  const handleCloseEditDialog = () => {
    setEditingQuestion(null);
    setSelectedTopicIds([]);
    setTopicSearchQuery('');
    setQuestionReports([]);
    setShowReportsSection(false);
    setExpandedReportIds(new Set());
    setEditFormData({
      question_text: '',
      answer: '',
      answer_images: [],
      topic_ids: [],
      asked: 1,
      asked_by_internal: false,
      surveyor_internal_id: null,
      surveyor_external_id: null,
      exam_month: '',
      exam_month_input: '',
      has_answer: false,
      is_decider: false,
      is_marq_recommended: false
    });
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;

    try {
      // Top-level surveyor / exam_month / asked fields are no longer part of
      // the edit modal — they're derived by the backend (recompute_from_history)
      // from the sittings array. We send only the fields the admin can still
      // edit here plus the sittings themselves. See deployment guide §3.10.
      const updateData = {
        question_text: editFormData.question_text,
        answer_text: editFormData.answer,
        answer_images: editFormData.answer_images,
        has_answer: editFormData.has_answer,
        video_url: editFormData.video_url || '',
        topic_ids: selectedTopicIds,
        topic_id: selectedTopicIds.length > 0 ? selectedTopicIds[0] : null, // Keep legacy field updated
        is_decider: editFormData.is_decider,
        is_marq_recommended: editFormData.is_marq_recommended,
        // Sittings — backend recomputes `asked`, latest surveyor, and
        // exam_month from this array. Sent only when at least one row has
        // a surveyor picked to avoid nuking existing history with a partial
        // edit.
        ...(editingSittings.some(s => s.surveyor_id)
          ? { asked_by_history: editingSittings.filter(s => s.surveyor_id) }
          : {}),
      };
      
      await axios.put(`${API}/questions/${editingQuestion.id}`, updateData);
      toast.success('Question updated successfully');
      handleCloseEditDialog();
      fetchQuestions();
      fetchQuickFilterStats();
    } catch (error) {
      console.error('Failed to update question:', error);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };
  
  // Handler for "Update & Resolve Reports" - updates question and resolves all pending reports
  const handleUpdateAndResolve = async () => {
    if (!editingQuestion) return;

    try {
      const updateData = {
        question_text: editFormData.question_text,
        answer_text: editFormData.answer,
        answer_images: editFormData.answer_images,
        has_answer: editFormData.has_answer,
        video_url: editFormData.video_url || '',
        topic_ids: selectedTopicIds,
        topic_id: selectedTopicIds.length > 0 ? selectedTopicIds[0] : null,
        is_decider: editFormData.is_decider,
        is_marq_recommended: editFormData.is_marq_recommended,
        ...(editingSittings.some(s => s.surveyor_id)
          ? { asked_by_history: editingSittings.filter(s => s.surveyor_id) }
          : {}),
      };
      
      // First update the question
      await axios.put(`${API}/questions/${editingQuestion.id}`, updateData);
      
      // Then resolve all pending reports
      const pendingReports = questionReports.filter(r => r.status === 'pending' || r.status === 'under_review');
      if (pendingReports.length > 0) {
        await axios.put(`${API}/reports/admin/resolve-all/${editingQuestion.id}?status=resolved`, null, {
          params: { admin_notes: 'Question updated based on report' }
        });
      }
      
      toast.success(`Question updated and ${pendingReports.length} report(s) resolved`);
      handleCloseEditDialog();
      fetchQuestions();
      fetchQuickFilterStats();
    } catch (error) {
      console.error('Failed to update question and resolve reports:', error);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };
  
  // Handler to resolve a single report
  const handleResolveReport = async (reportId) => {
    setResolvingReport(reportId);
    try {
      await axios.put(`${API}/reports/admin/${reportId}`, {
        status: 'resolved',
        admin_notes: 'Resolved by admin'
      });
      toast.success('Report resolved');
      // Refresh reports list
      setQuestionReports(prev => prev.map(r => 
        r.id === reportId ? { ...r, status: 'resolved' } : r
      ));
      fetchQuickFilterStats();
    } catch (error) {
      console.error('Failed to resolve report:', error);
      toast.error('Failed to resolve report');
    } finally {
      setResolvingReport(null);
    }
  };
  
  // Handler to dismiss a single report
  const handleDismissReport = async (reportId) => {
    setResolvingReport(reportId);
    try {
      await axios.put(`${API}/reports/admin/${reportId}`, {
        status: 'dismissed',
        admin_notes: 'Dismissed by admin'
      });
      toast.success('Report dismissed');
      // Refresh reports list
      setQuestionReports(prev => prev.map(r => 
        r.id === reportId ? { ...r, status: 'dismissed' } : r
      ));
      fetchQuickFilterStats();
    } catch (error) {
      console.error('Failed to dismiss report:', error);
      toast.error('Failed to dismiss report');
    } finally {
      setResolvingReport(null);
    }
  };

  // Topic selection handlers
  const handleAddTopic = (topicId) => {
    if (!selectedTopicIds.includes(topicId)) {
      setSelectedTopicIds([...selectedTopicIds, topicId]);
    }
    setTopicSearchQuery('');
  };

  const handleRemoveTopic = (topicId) => {
    setSelectedTopicIds(selectedTopicIds.filter(id => id !== topicId));
  };

  // Called by PictureManagerUploadDialog when each image upload succeeds
  const handlePMImageAdded = (image) => {
    setEditFormData(prev => {
      if (prev.answer_images.length >= 4) {
        toast.error('Maximum 4 images allowed per answer');
        return prev;
      }
      return {
        ...prev,
        answer_images: [
          ...prev.answer_images,
          { id: image.id, url: image.url, caption: image.caption || '', order: prev.answer_images.length }
        ]
      };
    });
  };

  // Image delete handler
  const handleImageDelete = async (imageId, imageUrl) => {
    // Remove from form state immediately (no spinner needed)
    setEditFormData(prev => ({
      ...prev,
      answer_images: prev.answer_images
        .filter(img => img.id !== imageId)
        .map((img, idx) => ({ ...img, order: idx }))
    }));
    toast.success('Image removed');

    // For legacy (non-PM) images, clean up the file on the server
    if (imageUrl && !imageUrl.includes('/uploads/managed_images/')) {
      const filename = imageUrl.split('/').pop();
      axios.delete(`${API}/admin/delete-answer-image/${filename}`).catch(() => {});
    }
  };

  // Update image caption
  const handleImageCaptionChange = (imageId, caption) => {
    setEditFormData(prev => ({
      ...prev,
      answer_images: prev.answer_images.map(img =>
        img.id === imageId ? { ...img, caption } : img
      )
    }));
  };

  // Handle adding image from library
  const handleAddFromLibrary = (image) => {
    if (editFormData.answer_images.length >= 4) {
      toast.error('Maximum 4 images allowed');
      return;
    }
    setEditFormData(prev => ({
      ...prev,
      answer_images: [...prev.answer_images, image]
    }));
    toast.success('Image added from library');
  };

  // Filter topics based on the question's grade and function
  const getFilteredTopics = (question) => {
    if (!question) return [];
    return topics.filter(
      t => t.grade === question.grade && t.function_id === question.function_id
    );
  };

  // Hierarchical version: parents first, each followed by its children.
  // Used in the edit-question modal so the topic picker mirrors the
  // parent/child structure shown elsewhere in the admin UI.
  const getFilteredTopicsHierarchical = (question) => {
    const available = getFilteredTopics(question);
    if (available.length === 0) return [];
    const byId = new Map(available.map(t => [t.id, t]));
    const parents = available.filter(t => !t.parent_topic_id);
    const orphans = available.filter(t => t.parent_topic_id && !byId.has(t.parent_topic_id));
    const ordered = [];
    parents.forEach(p => {
      ordered.push({ ...p, depth: 0 });
      available
        .filter(c => c.parent_topic_id === p.id)
        .forEach(c => ordered.push({ ...c, depth: 1, parent_name: p.name }));
    });
    // Stray children whose parent isn't in this grade/function pair —
    // render at depth 0 so they're not lost.
    orphans.forEach(o => ordered.push({ ...o, depth: 0 }));
    return ordered;
  };

  // Get topic name by ID
  const getTopicName = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : null;
  };

  // Get function info by ID
  const getFunctionInfo = (functionId) => {
    const func = functions.find(f => f.id === functionId);
    if (!func) return 'Unknown Function';
    const suffix = func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : '';
    return `F${func.function_number}${suffix} - ${func.name}`;
  };

  // Check if question has additional surveyor history (asked_by_history)
  const hasAskedByHistory = (question) => {
    return question.asked_by_history && question.asked_by_history.length > 0;
  };

  // Helper function to format exam month for display
  const formatExamMonth = (examMonth) => {
    if (!examMonth || examMonth.trim() === '') return null;
    const trimmed = examMonth.trim();
    
    // Try YYYY-MM format first
    if (/^\d{4}-\d{2}$/.test(trimmed)) {
      const date = new Date(trimmed + '-01');
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
    }
    
    // Try "Month YYYY" format
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const monthYearMatch = trimmed.match(/^(\w+)\s+(\d{4})$/);
    if (monthYearMatch) {
      const monthName = monthYearMatch[1];
      const year = monthYearMatch[2];
      const monthIndex = months.findIndex(m => m.toLowerCase() === monthName.toLowerCase());
      if (monthIndex !== -1) {
        const date = new Date(parseInt(year), monthIndex, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
    }
    
    return null;
  };

  // Get full history for display in modal - just use asked_by_history as-is
  // (Backend already includes original surveyor when merging)
  // Group by surveyor + exam_month for cleaner display
  const getFullHistory = (question) => {
    const history = question.asked_by_history || [];
    
    // If no history, show the original surveyor from top-level fields
    if (history.length === 0) {
      const originalSurveyorId = question.surveyor_internal_id || question.surveyor_external_id;
      const surveyorType = question.surveyor_internal_id ? 'Internal' : 'External';
      if (originalSurveyorId) {
        const surveyor = surveyors.find(s => s.id === originalSurveyorId);
        return [{
          surveyor_name: surveyor?.name || 'Unknown Surveyor',
          surveyor_type: surveyorType,
          exam_month: question.exam_month,
          mmd_location: question.mmd_location,
          count: 1
        }];
      }
      return [];
    }
    
    // Normalize the surveyor identifier so legacy "unknown" sentinels and
    // the proper "unknown-{type}-active" IDs are treated as the SAME group.
    // Without this, the same display name can appear on multiple rows.
    const SENTINEL_IDS = new Set(['unknown', 'unknown-internal-active', 'unknown-external-active']);
    const normSurveyorKey = (entry) => {
      const sid = entry.surveyor_id;
      if (!sid || SENTINEL_IDS.has(sid)) {
        return `_unknown_${entry.surveyor_type || 'external'}`;
      }
      return sid;
    };
    // Normalize exam_month: trim + lowercase + collapse whitespace. Null/empty
    // becomes a single "_undated" token so all dateless entries share a row.
    const normMonthKey = (em) => {
      if (!em || !String(em).trim()) return '_undated';
      return String(em).trim().toLowerCase().replace(/\s+/g, ' ');
    };

    // Group entries by normalized (surveyor, exam_month)
    const grouped = {};
    history.forEach(entry => {
      const surveyor = surveyors.find(s => s.id === entry.surveyor_id);
      const key = `${normSurveyorKey(entry)}|${normMonthKey(entry.exam_month)}`;

      // Friendly fallback for legacy rows where surveyor_id was saved as the
      // literal string "unknown" (pre-fix data): show "Unknown Internal" /
      // "Unknown External" based on surveyor_type instead of the raw id.
      const typeLabel = entry.surveyor_type === 'internal' ? 'Internal' : 'External';
      const fallbackName = `Unknown ${typeLabel}`;
      const displayName = surveyor?.name
        || entry.surveyor_name
        || ((!entry.surveyor_id || entry.surveyor_id === 'unknown') ? fallbackName : (entry.surveyor_id || fallbackName));

      if (!grouped[key]) {
        // For undated entries, derive a sensible display date: the entry's
        // own added_at month/year if present, else the current month/year.
        // This is a DISPLAY-only fill — the underlying data may still be null
        // until the maintenance backfill runs.
        let displayMonth = entry.exam_month;
        if (!displayMonth || !String(displayMonth).trim()) {
          const ts = entry.added_at ? new Date(entry.added_at) : new Date();
          if (!isNaN(ts.getTime())) {
            displayMonth = ts.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
          }
        }
        grouped[key] = {
          surveyor_name: displayName,
          surveyor_type: typeLabel,
          exam_month: displayMonth,
          mmd_location: entry.mmd_location,
          count: 0
        };
      }
      grouped[key].count += entry.asked || 1;
    });

    // Convert to array and sort by exam_month (newest first)
    return Object.values(grouped).sort((a, b) => {
      if (!a.exam_month) return 1;
      if (!b.exam_month) return -1;
      return b.exam_month.localeCompare(a.exam_month);
    });
  };

  // Filter questions by search query
  // Since we're using backend pagination, display questions as-is
  // Search would need to be implemented on backend if needed
  const filteredQuestions = useMemo(() => {
    let out = questions;
    if (reviewEnabled) {
      if (reviewFilter === 'true') out = out.filter((q) => !!q.needs_review);
      else if (reviewFilter === 'false') out = out.filter((q) => !q.needs_review);
      if (reviewCategoryFilter === 'uncategorized') {
        out = out.filter((q) => q.needs_review && !q.review_category_id);
      } else if (reviewCategoryFilter && reviewCategoryFilter !== 'all') {
        out = out.filter((q) => q.needs_review && q.review_category_id === reviewCategoryFilter);
      }
    }
    return out;
  }, [questions, reviewEnabled, reviewFilter, reviewCategoryFilter]);

  // Get oral functions filtered by selected grade
  const getOralFunctionsForGrade = useMemo(() => {
    if (selectedGrade === 'all') {
      // Return all oral functions when no grade selected
      return functions.filter(f => f.exam_type !== 'written');
    }
    return functions.filter(f => 
      f.grade === selectedGrade && f.exam_type !== 'written'
    );
  }, [functions, selectedGrade]);

  // Get filtered topics based on selected grade AND function (both required)
  const getAvailableTopics = () => {
    // Require both grade AND function to show topics
    if (selectedGrade === 'all' || selectedFunction === 'all') return [];
    
    // Filter topics by both grade and function
    return topics.filter(t => t.grade === selectedGrade && t.function_id === selectedFunction);
  };

  // Organize topics into parent-child hierarchy for display
  const getHierarchicalTopics = useMemo(() => {
    const availableTopics = getAvailableTopics();
    
    // Separate parents (no parent_topic_id) and children
    const parentTopics = availableTopics.filter(t => !t.parent_topic_id);
    const childTopics = availableTopics.filter(t => t.parent_topic_id);
    
    // Build hierarchy
    const hierarchy = [];
    parentTopics.forEach(parent => {
      hierarchy.push({ ...parent, isParent: true, level: 0 });
      const children = childTopics.filter(c => c.parent_topic_id === parent.id);
      children.forEach(child => {
        hierarchy.push({ ...child, isParent: false, level: 1 });
      });
    });
    
    // Add orphan children (topics with parent_topic_id but parent not in current filter)
    const addedChildIds = new Set(hierarchy.filter(t => t.level === 1).map(t => t.id));
    childTopics.forEach(child => {
      if (!addedChildIds.has(child.id)) {
        hierarchy.push({ ...child, isParent: false, level: 0 }); // Show as top-level
      }
    });
    
    return hierarchy;
  }, [topics, selectedGrade, selectedFunction]);

  // Get topic display name with parent info
  const getTopicDisplayName = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    if (!topic) return 'Unknown Topic';
    
    if (topic.parent_topic_id) {
      const parent = topics.find(t => t.id === topic.parent_topic_id);
      return parent ? `${parent.name} → ${topic.name}` : topic.name;
    }
    return topic.name;
  };

  // Topic selector open state
  const [topicSelectorOpen, setTopicSelectorOpen] = useState(false);

  // Export functions
  const handleExportClick = () => {
    // Only admins can export
    if (!isFullAdmin) {
      toast.error('Admin rights required to export questions');
      return;
    }
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportFilters({
      grade: 'all',
      mmd_location: 'all',
      function_id: 'all',
      topic_id: 'all',
      surveyor_internal_id: 'all',
      surveyor_external_id: 'all',
      exam_month: '',
      decider_only: false
    });
  };

  const handleExport = async () => {
    // Double-check admin rights
    if (!isFullAdmin) {
      toast.error('Admin rights required to export questions');
      return;
    }
    
    try {
      setExporting(true);
      
      // Build query params for new export API
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      if (exportFilters.grade !== 'all') params.append('grade', exportFilters.grade);
      if (exportFilters.mmd_location !== 'all') params.append('mmd_location', exportFilters.mmd_location);
      if (exportFilters.function_id !== 'all') params.append('function_id', exportFilters.function_id);
      if (exportFilters.surveyor_internal_id !== 'all') params.append('surveyor_id', exportFilters.surveyor_internal_id);
      if (exportFilters.status !== 'all') params.append('status', exportFilters.status);

      const response = await axios.get(`${API}/export/oral-questions?${params}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      const extension = exportFormat === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `oral_questions_export_${dateStr}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Questions exported successfully');
      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export questions');
    } finally {
      setExporting(false);
    }
  };

  // Fetch export count when dialog opens or filters change
  const fetchExportCount = async () => {
    try {
      const params = new URLSearchParams();
      if (exportFilters.grade !== 'all') params.append('grade', exportFilters.grade);
      if (exportFilters.mmd_location !== 'all') params.append('mmd_location', exportFilters.mmd_location);
      if (exportFilters.function_id !== 'all') params.append('function_id', exportFilters.function_id);
      if (exportFilters.surveyor_internal_id !== 'all') params.append('surveyor_id', exportFilters.surveyor_internal_id);
      
      const response = await axios.get(`${API}/export/oral-questions/count?${params}`);
      setExportCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch export count:', error);
    }
  };

  useEffect(() => {
    if (exportDialogOpen) {
      fetchExportCount();
    }
  }, [exportDialogOpen, exportFilters]);

  return (
    <Layout>
      <div className="page-transition">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/content')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Content Management
        </Button>
        
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-[#10B981]/5 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#10B981] rounded-lg">
                  <Mic className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Oral Question Management</CardTitle>
                  <CardDescription>Browse and manage oral exam questions by grade and function</CardDescription>
                </div>
              </div>
              
              {/* Header actions — mirrors WrittenManagementPage layout:
                  a single "More" dropdown for lower-frequency bulk ops
                  plus a dedicated Export button. Keeps the header to a
                  single row on mid-size screens. */}
              {isFullAdmin && (
                <div className="flex flex-wrap gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-300 text-gray-700 hover:bg-gray-50"
                        data-testid="oral-more-actions-button"
                      >
                        <MoreHorizontal className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">More</span>
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-56"
                      data-testid="oral-more-actions-menu"
                    >
                      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-gray-500">
                        Answers
                      </DropdownMenuLabel>
                      <BulkAnswerControls
                        questionType="oral"
                        onCommitted={() => fetchQuestions && fetchQuestions()}
                        compact
                      />
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-gray-500">
                        Bulk selection
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          if (!canEnableBulk) {
                            toast.info('Set Grade + Function filters to enable bulk actions.');
                            return;
                          }
                          setBulkMode((v) => {
                            const next = !v;
                            if (!next) setBulkSelectedIds(new Set());
                            return next;
                          });
                        }}
                        disabled={!canEnableBulk}
                        className={bulkMode ? 'text-indigo-700 focus:bg-indigo-50' : ''}
                        data-testid="bulk-mode-toggle"
                      >
                        <Layers className="w-4 h-4 mr-2" />
                        {bulkMode ? 'Turn off bulk actions' : 'Enable bulk actions'}
                      </DropdownMenuItem>
                      {!canEnableBulk && (
                        <div className="px-2 pb-2 text-[10px] text-gray-500 leading-snug">
                          Requires Grade + Function filters to be set first.
                        </div>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    onClick={handleExportClick}
                    size="sm"
                    className="bg-[#10B981] hover:bg-[#10B981]/90"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Questions
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Filters and Search */}
        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            {/* Search Bar */}
            <div>
              <label className="text-sm font-medium mb-2 block">Search Questions</label>
              <Input
                type="text"
                placeholder="Search by question text, answer text, or question ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Searches across all pages</p>
            </div>

            {/* Quick Filter Pills */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  showFlaggedOnly 
                    ? 'bg-red-500 text-white shadow-md' 
                    : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                }`}
              >
                <Flag className="w-3.5 h-3.5" />
                Flagged
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  showFlaggedOnly ? 'bg-red-400' : 'bg-red-100'
                }`}>
                  {quickFilterStats.flagged}
                </span>
              </button>
              
              <button
                onClick={() => setShowUnansweredOnly(!showUnansweredOnly)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  showUnansweredOnly 
                    ? 'bg-amber-500 text-white shadow-md' 
                    : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                }`}
              >
                <FileQuestion className="w-3.5 h-3.5" />
                No Answer
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  showUnansweredOnly ? 'bg-amber-400' : 'bg-amber-100'
                }`}>
                  {quickFilterStats.unanswered}
                </span>
              </button>
              
              <button
                onClick={() => setShowNoTopicOnly(!showNoTopicOnly)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  showNoTopicOnly 
                    ? 'bg-purple-500 text-white shadow-md' 
                    : 'bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100'
                }`}
              >
                <FolderX className="w-3.5 h-3.5" />
                No Topic
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                  showNoTopicOnly ? 'bg-purple-400' : 'bg-purple-100'
                }`}>
                  {quickFilterStats.no_topic}
                </span>
              </button>

              {/* Iter 113: Needs Review filter — only visible to admin/sub-admin
                  when server-side review access is granted. Client-side
                  filter operates on the already-fetched page (matches the
                  scope discussed with the user). */}
              {reviewEnabled && (
                <button
                  onClick={() => setReviewFilter(reviewFilter === 'true' ? 'any' : 'true')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    reviewFilter === 'true'
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                  }`}
                  data-testid="needs-review-filter-chip"
                >
                  <ClipboardPen className="w-3.5 h-3.5" />
                  Needs Review
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    reviewFilter === 'true' ? 'bg-amber-400' : 'bg-amber-100'
                  }`}>
                    {questions.filter((q) => q.needs_review).length}
                  </span>
                </button>
              )}
            </div>

            {/* Iter 113: Category sub-filter — shown only when the Needs Review
                chip is active and at least one category exists. */}
            {reviewEnabled && reviewFilter === 'true' && reviewCategories.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap" data-testid="review-category-filter">
                <span className="text-xs text-gray-500">Category:</span>
                {[
                  { id: 'all', name: 'All' },
                  { id: 'uncategorized', name: 'Uncategorized' },
                  ...reviewCategories,
                ].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setReviewCategoryFilter(c.id)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                      reviewCategoryFilter === c.id
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                    }`}
                    data-testid={`review-category-filter-${c.id}`}
                  >
                    {c.color && <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />}
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            {/* Collapsible: Date Range Filter */}
            <div className="border border-blue-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowDateAdminFilters(!showDateAdminFilters)}
                className="w-full p-3 bg-blue-50 flex items-center justify-between hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-blue-700" />
                  <span className="text-sm font-semibold text-blue-900">Date & Admin Filters</span>
                  {(dateRangeType !== 'none' || filterByAdmin !== 'none') && (
                    <Badge variant="secondary" className="text-xs">Active</Badge>
                  )}
                </div>
                {showDateAdminFilters ? (
                  <ChevronDown className="w-4 h-4 text-blue-700" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-blue-700" />
                )}
              </button>
              
              {showDateAdminFilters && (
                <div className="p-4 bg-blue-50/50 space-y-4">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDateRangeType('none');
                        setDateFrom('');
                        setDateTo('');
                        setFilterByAdmin('none');
                        setAdminIndos('');
                      }}
                      className="text-xs"
                    >
                      Clear Filters
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Date Range Type</label>
                      <Select value={dateRangeType} onValueChange={setDateRangeType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="exam">Exam Time</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="modified">Modified</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">From Month/Year</label>
                      <MonthPicker
                        value={dateFrom}
                        onChange={setDateFrom}
                        disabled={dateRangeType === 'none'}
                        placeholder="Select start month"
                        fromYear={2015}
                        toYear={new Date().getFullYear()}
                        maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        To Month/Year <span className="text-xs text-gray-500">(empty = present)</span>
                      </label>
                      <MonthPicker
                        value={dateTo}
                        onChange={setDateTo}
                        disabled={dateRangeType === 'none'}
                        minMonth={dateFrom}
                        placeholder="Select end month"
                        fromYear={2015}
                        toYear={new Date().getFullYear()}
                        maxMonth={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`}
                      />
                    </div>
                  </div>
                  
                  {/* Admin Filter */}
                  <div className="grid md:grid-cols-2 gap-4 pt-3 border-t border-blue-300">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Filter By Admin</label>
                      <Select value={filterByAdmin} onValueChange={setFilterByAdmin}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select filter type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="approved">Approved By</SelectItem>
                          <SelectItem value="modified">Last Modified By</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Admin INDoS Number <span className="text-xs text-gray-500">(partial match supported)</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Enter INDoS number"
                        value={adminIndos}
                        onChange={(e) => setAdminIndos(e.target.value)}
                        disabled={filterByAdmin === 'none'}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Collapsible: Sorting Controls */}
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowSortFilters(!showSortFilters)}
                className="w-full p-3 bg-amber-50 flex items-center justify-between hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-amber-700" />
                  <span className="text-sm font-semibold text-amber-900">Sort Questions</span>
                  <Badge variant="secondary" className="text-xs">
                    {sortBy === 'approved' ? 'Approved' : sortBy === 'exam' ? 'Exam' : 'Modified'} • {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
                  </Badge>
                </div>
                {showSortFilters ? (
                  <ChevronDown className="w-4 h-4 text-amber-700" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-amber-700" />
                )}
              </button>
              
              {showSortFilters && (
                <div className="p-4 bg-amber-50/50">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Sort By</label>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exam">Exam Time</SelectItem>
                          <SelectItem value="approved">Approved Date</SelectItem>
                          <SelectItem value="modified">Last Modified Date</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Sort Order</label>
                      <Select value={sortOrder} onValueChange={setSortOrder}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="desc">Newest First</SelectItem>
                          <SelectItem value="asc">Oldest First</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Independent Filters: Grade, Function, MMD */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Grade</label>
                <Select 
                  value={selectedGrade} 
                  onValueChange={(value) => {
                    setSelectedGrade(value);
                    setSelectedFunction('all'); // Reset function when grade changes
                    setSelectedTopic('all'); // Reset topic when grade changes
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select grade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {grades.map((grade) => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Function (Oral Only)
                  {selectedGrade === 'all' && <span className="text-gray-500 text-xs ml-1">(All grades)</span>}
                </label>
                <Select 
                  value={selectedFunction} 
                  onValueChange={(value) => {
                    setSelectedFunction(value);
                    setSelectedTopic('all'); // Reset topic when function changes
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Oral Functions</SelectItem>
                    {getOralFunctionsForGrade.map((func) => (
                      <SelectItem key={func.id} value={func.id}>
                        F{func.function_number}{func.function_suffix && func.function_suffix !== 'None' && func.function_suffix !== 'null' ? func.function_suffix : ''} - {func.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">MMD Location</label>
                <Select value={selectedMMD} onValueChange={setSelectedMMD}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {mmdLocations.map((mmd) => (
                      <SelectItem key={mmd} value={mmd}>{mmd}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dependent Filters */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* Topic - Searchable with Parent-Child Hierarchy */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Topic 
                  {(selectedGrade === 'all' || selectedFunction === 'all') && (
                    <span className="text-gray-500 text-xs ml-1">(Select Grade & Function first)</span>
                  )}
                </label>
                <TopicPicker
                  topics={getAvailableTopics()}
                  value={selectedTopic === 'all' ? null : selectedTopic}
                  onChange={(id) => setSelectedTopic(id || 'all')}
                  placeholder={
                    selectedGrade === 'all' || selectedFunction === 'all'
                      ? 'Select grade & function first…'
                      : 'All Topics'
                  }
                  allowClear
                  disabled={selectedGrade === 'all' || selectedFunction === 'all'}
                  testIdPrefix="admin-oral-topic-filter"
                />
                {selectedTopic !== 'all' && topics.find(t => t.id === selectedTopic) && (
                  <p className="text-xs text-amber-600 mt-1">
                    ↳ Showing questions from this topic and all sub-topics
                  </p>
                )}
              </div>

              {/* Internal Surveyor - Searchable */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Internal Surveyor
                </label>
                <Popover open={internalSurveyorComboOpen} onOpenChange={setInternalSurveyorComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={internalSurveyorComboOpen}
                      className="w-full justify-between"
                    >
                      {selectedInternalSurveyor === 'all'
                        ? 'All Internal Surveyors'
                        : surveyors.find(s => s.id === selectedInternalSurveyor)?.name || 'Select surveyor'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search internal surveyors..." />
                      <CommandList>
                        <CommandEmpty>No surveyors found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedInternalSurveyor('all');
                              setInternalSurveyorComboOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedInternalSurveyor === 'all' ? "opacity-100" : "opacity-0")} />
                            All Internal Surveyors
                          </CommandItem>
                        </CommandGroup>
                        {/* Active Internal Surveyors */}
                        {surveyors.filter(s => s.type === 'internal' && s.is_active !== false).length > 0 && (
                          <CommandGroup heading="Active">
                            {surveyors.filter(s => s.type === 'internal' && s.is_active !== false).map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={surveyor.name}
                                onSelect={() => {
                                  setSelectedInternalSurveyor(surveyor.id);
                                  setInternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedInternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {/* Inactive Internal Surveyors */}
                        {surveyors.filter(s => s.type === 'internal' && s.is_active === false).length > 0 && (
                          <CommandGroup heading="Past Surveyors">
                            {surveyors.filter(s => s.type === 'internal' && s.is_active === false).map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={`${surveyor.name} inactive`}
                                onSelect={() => {
                                  setSelectedInternalSurveyor(surveyor.id);
                                  setInternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedInternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name} (Inactive)
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* External Surveyor - Searchable */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  External Surveyor
                </label>
                <Popover open={externalSurveyorComboOpen} onOpenChange={setExternalSurveyorComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={externalSurveyorComboOpen}
                      className="w-full justify-between"
                    >
                      {selectedExternalSurveyor === 'all'
                        ? 'All External Surveyors'
                        : surveyors.find(s => s.id === selectedExternalSurveyor)?.name || 'Select surveyor'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search external surveyors..." />
                      <CommandList>
                        <CommandEmpty>No surveyors found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value="all"
                            onSelect={() => {
                              setSelectedExternalSurveyor('all');
                              setExternalSurveyorComboOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", selectedExternalSurveyor === 'all' ? "opacity-100" : "opacity-0")} />
                            All External Surveyors
                          </CommandItem>
                        </CommandGroup>
                        {/* Active External Surveyors */}
                        {surveyors.filter(s => s.type === 'external' && s.is_active !== false).length > 0 && (
                          <CommandGroup heading="Active">
                            {surveyors.filter(s => s.type === 'external' && s.is_active !== false).map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={surveyor.name}
                                onSelect={() => {
                                  setSelectedExternalSurveyor(surveyor.id);
                                  setExternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedExternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                        {/* Inactive External Surveyors */}
                        {surveyors.filter(s => s.type === 'external' && s.is_active === false).length > 0 && (
                          <CommandGroup heading="Past Surveyors">
                            {surveyors.filter(s => s.type === 'external' && s.is_active === false).map((surveyor) => (
                              <CommandItem
                                key={surveyor.id}
                                value={`${surveyor.name} inactive`}
                                onSelect={() => {
                                  setSelectedExternalSurveyor(surveyor.id);
                                  setExternalSurveyorComboOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", selectedExternalSurveyor === surveyor.id ? "opacity-100" : "opacity-0")} />
                                {surveyor.name} (Inactive)
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ─── Bulk Action Bar (sticky under app header when scrolled) ────
            Sits inline BELOW the filters card so admins first see it in
            context — then it sticks to the top (`top-16`, matching the
            Layout's sticky header height) as they scroll through the
            results. `position: sticky` handles the transition natively
            — zero JS listeners. */}
        {bulkMode && bulkSelectedIds.size > 0 && (
          <div
            className="sticky z-30 bg-white border border-slate-200 rounded-lg shadow-lg mb-4"
            style={{ top: `${headerH}px` }}
            data-testid="bulk-action-bar"
          >
            <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-xs">
                  {bulkSelectedIds.size}
                </div>
                <span>selected</span>
              </div>

              <Popover open={bulkPopoverOpen} onOpenChange={(v) => { setBulkPopoverOpen(v); if (!v) setBulkReplaceConfirmed(false); }}>
                <PopoverTrigger asChild>
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="bulk-topics-open">
                    <Tag className="w-4 h-4 mr-2" />
                    Set Topics
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[460px] p-3" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Mode</Label>
                      <div className="flex rounded border border-gray-300 overflow-hidden text-[10px]">
                        {[
                          { v: 'add',     label: 'Add',     hint: 'Union with each question\'s existing topics (ancestors pruned)' },
                          { v: 'replace', label: 'Replace', hint: 'Overwrite each question\'s topics — destructive' },
                          { v: 'remove',  label: 'Remove',  hint: 'Subtract these topics from each question\'s set' },
                        ].map((opt) => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => {
                              setBulkTopicMode(opt.v);
                              setBulkReplaceConfirmed(false);
                              try { localStorage.setItem('oral-bulk-topic-mode-v1', opt.v); } catch { /* quota */ }
                            }}
                            className={`px-2 py-0.5 border-r last:border-r-0 border-gray-300 transition-colors ${
                              bulkTopicMode === opt.v
                                ? (opt.v === 'replace' ? 'bg-amber-500 text-white' : opt.v === 'remove' ? 'bg-rose-500 text-white' : 'bg-emerald-600 text-white')
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                            title={opt.hint}
                            data-testid={`bulk-topics-mode-${opt.v}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {bulkTopicMode === 'add' && 'Selected topics will be added to each question. Ancestor topics are auto-pruned when a descendant is present.'}
                      {bulkTopicMode === 'replace' && 'Each question\'s existing topics will be overwritten.'}
                      {bulkTopicMode === 'remove' && 'Selected topics will be removed from each question (untouched if not present).'}
                    </div>
                    <TopicPickerMulti
                      topics={topics}
                      value={bulkTopicIds}
                      onChange={setBulkTopicIds}
                      placeholder={bulkTopicMode === 'remove' ? 'Topics to remove…' : 'Assign topics…'}
                      testIdPrefix="bulk-topic"
                    />

                    {bulkTopicMode === 'replace' && !bulkReplaceConfirmed && (
                      <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="bulk-replace-confirm">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          This will overwrite topics on {bulkSelectedIds.size} question(s). Existing topics will be lost.
                        </div>
                        <button
                          type="button"
                          onClick={() => setBulkReplaceConfirmed(true)}
                          className="flex-shrink-0 px-2 py-0.5 bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-medium rounded"
                        >
                          I understand
                        </button>
                      </div>
                    )}

                    <div className="flex justify-between pt-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]"
                        onClick={() => setBulkTopicIds([])}>Clear picker</Button>
                      <Button
                        size="sm"
                        className={`h-8 px-3 text-xs ${
                          bulkTopicMode === 'replace' ? 'bg-amber-600 hover:bg-amber-700' :
                          bulkTopicMode === 'remove'  ? 'bg-rose-600 hover:bg-rose-700' :
                                                        'bg-indigo-600 hover:bg-indigo-700'
                        }`}
                        onClick={async () => {
                          if (bulkTopicMode !== 'replace' && bulkTopicIds.length === 0) return;
                          if (bulkTopicMode === 'replace' && !bulkReplaceConfirmed) return;
                          try {
                            setBulkBusy(true);
                            const res = await axios.post(`${API}/admin/questions/bulk-edit-topics`, {
                              question_ids: Array.from(bulkSelectedIds),
                              topic_ids: bulkTopicIds,
                              mode: bulkTopicMode,
                            });
                            toast.success(`Updated ${res.data?.modified_count ?? 0} question(s).`);
                            setBulkPopoverOpen(false);
                            setBulkReplaceConfirmed(false);
                            setBulkTopicIds([]);
                            fetchQuestions();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Bulk topic edit failed');
                          } finally {
                            setBulkBusy(false);
                          }
                        }}
                        disabled={bulkBusy || (bulkTopicMode !== 'replace' && bulkTopicIds.length === 0) || (bulkTopicMode === 'replace' && !bulkReplaceConfirmed)}
                        data-testid="bulk-topics-apply"
                      >
                        {bulkBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                        {bulkTopicMode === 'add' && `Add to ${bulkSelectedIds.size}`}
                        {bulkTopicMode === 'replace' && `Replace on ${bulkSelectedIds.size}`}
                        {bulkTopicMode === 'remove' && `Remove from ${bulkSelectedIds.size}`}
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <div className="flex-1" />
              <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => setBulkSelectedIds(new Set())} data-testid="bulk-clear-selection">
                Clear selection
              </Button>
            </div>
          </div>
        )}

        {/* Questions List */}
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No questions found for the selected filters or search query</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    {totalItems} Question{totalItems !== 1 ? 's' : ''} Found
                  </h3>
                  {bulkMode && (() => {
                    const visibleIds = filteredQuestions.map((q) => q.id);
                    const visibleSelected = visibleIds.filter((id) => bulkSelectedIds.has(id)).length;
                    const allChecked = visibleIds.length > 0 && visibleSelected === visibleIds.length;
                    const someChecked = visibleSelected > 0 && !allChecked;
                    const invisibleSelected = bulkSelectedIds.size - visibleSelected;
                    return (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mt-2 flex-wrap">
                        <Checkbox
                          checked={allChecked || (someChecked ? 'indeterminate' : false)}
                          onCheckedChange={(checked) => {
                            setBulkSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (checked) visibleIds.forEach((id) => next.add(id));
                              else visibleIds.forEach((id) => next.delete(id));
                              return next;
                            });
                          }}
                          data-testid="bulk-select-all-visible"
                          aria-label="Select all visible questions"
                        />
                        <span className="font-medium text-gray-700">
                          {allChecked ? 'All visible selected' : someChecked ? `${visibleSelected} of ${visibleIds.length} visible selected` : 'Select all visible'}
                        </span>
                        {invisibleSelected > 0 && (
                          <span className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5" data-testid="bulk-invisible-hint">
                            {invisibleSelected} more selected outside current view
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {filteredQuestions.map((question, index) => (
                  <Card key={question.id} className={`border transition-colors ${bulkSelectedIds.has(question.id) ? 'border-indigo-400 bg-indigo-50/30' : 'hover:border-[#10B981]'}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start gap-4">
                        {bulkMode && (
                          <div className="flex-shrink-0 pt-1">
                            <Checkbox
                              checked={bulkSelectedIds.has(question.id)}
                              onCheckedChange={(checked) => {
                                setBulkSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (checked) next.add(question.id);
                                  else next.delete(question.id);
                                  return next;
                                });
                              }}
                              aria-label={`Select question ${question.id}`}
                              data-testid={`bulk-select-${question.id}`}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-800 mb-2">{question.question_text}</p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-[#0A7EA4]">{question.grade}</Badge>
                            <Badge className="bg-[#14B8A6]">{question.mmd_location}</Badge>
                            <Badge className="bg-orange-500">{getFunctionInfo(question.function_id)}</Badge>
                            <Badge variant="outline">{question.status}</Badge>
                            {question.has_answer && (
                              <Badge className="bg-green-600">Answered</Badge>
                            )}
                            {question.is_decider && (
                              <Badge className="bg-[#FF6B6B]">Decider</Badge>
                            )}
                            {question.is_marq_recommended && (
                              <Badge className="bg-purple-600">MARQ Recommended</Badge>
                            )}
                            {question.asked > 1 && (
                              <Badge className="bg-orange-600">Asked {question.asked}x</Badge>
                            )}
                            {/* Iter 113: Needs Review badge — follows reviewEnabled from admin review settings */}
                            {reviewEnabled && (
                              <ReviewBadge
                                question={question}
                                category={getReviewCategory(question.review_category_id)}
                                dataTestId={`review-badge-${question.id}`}
                              />
                            )}
                            {/* History icon - shows when there are multiple surveyors who asked this question */}
                            {hasAskedByHistory(question) && (
                              <button
                                onClick={() => setHistoryModal({ open: true, question })}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border border-blue-300 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors cursor-pointer"
                                title="View all surveyors who asked this question"
                              >
                                <History className="w-3 h-3" />
                                +{question.asked_by_history.length} more
                              </button>
                            )}
                            {/* Show flag indicator when question has pending user reports */}
                            {question.has_pending_reports && (
                              <Badge className="bg-red-500 flex items-center gap-1">
                                <Flag className="w-3 h-3" />
                                Flagged
                              </Badge>
                            )}
                            {/* Display multiple topics */}
                            {(question.topic_ids?.length > 0 || question.topic_id) ? (
                              (question.topic_ids?.length > 0 ? question.topic_ids : [question.topic_id])
                                .map(tid => getTopicName(tid))
                                .filter(Boolean)
                                .map((topicName, idx) => (
                                  <Badge key={idx} className="bg-[#8B5CF6] flex items-center gap-1">
                                    <Tag className="w-3 h-3" />
                                    {topicName}
                                  </Badge>
                                ))
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                No Topic
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingQuestion(question)}
                            className="text-[#0A7EA4] hover:text-[#0A7EA4]"
                            title="View Question"
                            data-testid={`view-question-btn-${question.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditQuestion(question)}
                            className="text-[#8B5CF6] hover:text-[#8B5CF6]"
                            title="Edit Question"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {/* Delete button - Admin only */}
                          {isFullAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteQuestion(question.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Pagination */}
                {filteredQuestions.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onItemsPerPageChange={(perPage) => {
                      setItemsPerPage(perPage);
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Question Modal */}
      <QuestionViewModal
        question={viewingQuestion}
        kind="oral"
        open={!!viewingQuestion}
        onClose={() => setViewingQuestion(null)}
        onEdit={() => handleEditQuestion(viewingQuestion)}
        helpers={{
          getFunctionName: getFunctionInfo,
          getTopicName,
          getReviewCategory,
        }}
        reviewEnabled={reviewEnabled}
      />

      {/* Edit Question Dialog */}
      <Dialog open={!!editingQuestion} onOpenChange={handleCloseEditDialog}>
        <DialogContent className="sm:max-w-[1200px] w-[85vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#8B5CF6]" />
              Edit Question
            </DialogTitle>
            <DialogDescription>
              Edit the question text, answer, and topic assignment
            </DialogDescription>
            {editingQuestion?.id && (
              <div
                className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500"
                data-testid="admin-edit-question-id"
              >
                <span className="font-medium text-gray-600">Question ID:</span>
                <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-[11px] text-gray-800 select-all">
                  {editingQuestion.id}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(editingQuestion.id);
                    toast.success('Question ID copied');
                  }}
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                  title="Copy ID"
                  data-testid="admin-edit-question-id-copy"
                >
                  copy
                </button>
                {reviewEnabled && (
                  <ReviewChip
                    question={editingQuestion}
                    kind="oral"
                    categories={reviewCategories}
                    enabled={reviewEnabled}
                    onSaved={(updated) => {
                      setEditingQuestion((prev) => (prev ? { ...prev, ...updated } : prev));
                      setQuestions((prev) => prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)));
                    }}
                  />
                )}
              </div>
            )}
          </DialogHeader>

          {/* ── Always-visible: tag context + question text editing ── */}
          {editingQuestion && (
            <div className="space-y-3 pt-1 pb-4 border-b border-gray-200 mb-1">
              {/* Classification tags — read-only context */}
              <div className="flex flex-wrap gap-1.5">
                <Badge className="bg-[#0A7EA4] text-xs">{editingQuestion.grade}</Badge>
                <Badge className="bg-[#14B8A6] text-xs">
                  <MapPin className="w-2.5 h-2.5 mr-1" />{editingQuestion.mmd_location}
                </Badge>
                <Badge className="bg-orange-500 text-xs">
                  {getFunctionInfo(editingQuestion.function_id)}
                </Badge>
              </div>
              {/* Question text editing */}
              <div>
                <Label htmlFor="question-text" className="text-sm font-semibold text-gray-700">
                  Question Text <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="question-text"
                  value={editFormData.question_text}
                  onChange={(e) => setEditFormData({ ...editFormData, question_text: e.target.value })}
                  placeholder="Enter the question text..."
                  rows={3}
                  className="mt-1.5"
                  required
                />
                {/* Inline collapsible — original question */}
                {editingQuestion?.question_text && (
                  <div className="mt-1.5">
                    <button
                      type="button"
                      onClick={() => setShowOriginalQuestion(v => !v)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors select-none"
                      data-testid="show-original-question-toggle"
                    >
                      <ChevronRight className={`w-3 h-3 transition-transform duration-150 ${showOriginalQuestion ? 'rotate-90' : ''}`} />
                      {showOriginalQuestion ? 'Hide original' : 'Show original'}
                    </button>
                    {showOriginalQuestion && (
                      <div className="mt-1.5 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" data-testid="original-question-text">
                        {editingQuestion.question_text}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Fullscreen answer expand overlay (inside dialog stacking context) ── */}
          {answerExpanded && (
            <div
              className="absolute inset-0 z-50 bg-white flex flex-col rounded-lg"
              data-testid="answer-expand-overlay"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 flex-shrink-0 rounded-t-lg">
                <span className="font-semibold text-gray-800 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#8B5CF6]" />
                  Answer — {editingQuestion?.grade} · {getFunctionInfo(editingQuestion?.function_id)}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">{editFormData.answer?.length || 0} chars</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setAnswerExpanded(false)}
                    className="text-gray-600 hover:text-gray-900"
                    data-testid="answer-expand-close"
                  >
                    <Minimize2 className="w-4 h-4 mr-1.5" />
                    Done
                  </Button>
                </div>
              </div>
              <div className="flex-1 p-5 min-h-0">
                <Textarea
                  value={editFormData.answer}
                  onChange={(e) => setEditFormData({ ...editFormData, answer: e.target.value })}
                  placeholder="Enter the answer..."
                  className="h-full w-full resize-none text-sm leading-relaxed"
                  autoFocus
                  data-testid="answer-expand-textarea"
                />
              </div>
              <div className="px-5 py-2.5 border-t bg-gray-50 text-xs text-gray-400 flex-shrink-0 rounded-b-lg">
                Tip: Add links using [Link Text](url) format · Changes sync automatically with the modal
              </div>
            </div>
          )}

          {editingQuestion && (
            <Tabs value={editTab} onValueChange={setEditTab} className="mt-2">
              <TabsList className="w-full justify-start rounded-none bg-transparent h-auto p-0 border-b border-gray-200 mb-5">
                <TabsTrigger
                  value="content"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#8B5CF6] data-[state=active]:text-[#8B5CF6] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-sm font-medium"
                  data-testid="edit-tab-content"
                >
                  Content
                </TabsTrigger>
                <TabsTrigger
                  value="metadata"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#8B5CF6] data-[state=active]:text-[#8B5CF6] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2 text-sm font-medium"
                  data-testid="edit-tab-metadata"
                >
                  Metadata
                </TabsTrigger>
              </TabsList>

              {/* ──────────── CONTENT TAB ──────────── */}
              <TabsContent value="content" className="space-y-5 mt-0">

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="answer" className="text-base font-semibold">Answer</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setAnswerExpanded(true)}
                        className="text-gray-500 hover:text-gray-800 hover:bg-gray-100 h-7 text-xs"
                        data-testid="answer-expand-btn"
                      >
                        <Maximize2 className="w-3.5 h-3.5 mr-1" />
                        Expand
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowResearch(true)}
                        className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 h-7 text-xs"
                        data-testid="research-toggle-btn"
                      >
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        Research
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    id="answer"
                    value={editFormData.answer}
                    onChange={(e) => setEditFormData({ ...editFormData, answer: e.target.value })}
                    placeholder="Enter the answer (optional)..."
                    rows={10}
                    className="mt-2"
                  />
                  {/* Inline collapsible — original answer */}
                  {editingQuestion?.answer_text && (
                    <div className="mt-1.5">
                      <button
                        type="button"
                        onClick={() => setShowOriginalAnswer(v => !v)}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors select-none"
                        data-testid="show-original-answer-toggle"
                      >
                        <ChevronRight className={`w-3 h-3 transition-transform duration-150 ${showOriginalAnswer ? 'rotate-90' : ''}`} />
                        {showOriginalAnswer ? 'Hide original' : 'Show original'}
                      </button>
                      {showOriginalAnswer && (
                        <div className="mt-1.5 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" data-testid="original-answer-text">
                          {editingQuestion.answer_text}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty if no answer is available yet. 
                    <span className="text-blue-600 ml-1">Tip: Add links using [Link Text](url) format</span>
                  </p>
                </div>

                {/* Explainer video URL (YouTube or Dadan only) */}
                <div>
                  <Label htmlFor="video_url" className="text-sm font-medium">
                    Explainer video <span className="text-gray-500 font-normal">(optional · YouTube or Dadan)</span>
                  </Label>
                  <Input
                    id="video_url"
                    type="text"
                    value={editFormData.video_url || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, video_url: e.target.value })}
                    placeholder="Paste a YouTube/Dadan URL or <iframe> embed code (dadan.io or video.helmtorq.com)"
                    className="mt-2"
                    data-testid="admin-edit-video-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A &ldquo;Watch video&rdquo; button will appear next to the answer for paid users.
                    Accepts bare URL, share link, or full iframe embed code.
                  </p>
                </div>

                {/* Answer Images */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Answer Images (Max 4, 300KB each)</Label>
                    <span className="text-xs text-gray-500">{editFormData.answer_images.length}/4 images</span>
                  </div>
                  
                  {/* Image List */}
                  {editFormData.answer_images.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {editFormData.answer_images.map((image, index) => (
                        <div key={image.id} className="flex gap-2 p-2 bg-gray-50 rounded-lg border">
                          <div className="w-16 h-16 flex-shrink-0 bg-white rounded border overflow-hidden">
                            <img 
                              src={`${BACKEND_URL}${image.url}`} 
                              alt={`Answer ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <Input
                              placeholder="Caption..."
                              value={image.caption || ''}
                              onChange={(e) => handleImageCaptionChange(image.id, e.target.value)}
                              className="text-xs h-8"
                            />
                            <p className="text-xs text-gray-500 mt-1">Image {index + 1}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
                            onClick={() => handleImageDelete(image.id, image.url)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload Button */}
                  {editFormData.answer_images.length < 4 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPmUploadOpen(true)}
                        className="flex-1"
                        data-testid="upload-new-image-btn"
                      >
                        <ImagePlus className="h-4 w-4 mr-2" />
                        Upload New
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setImagePickerOpen(true)}
                        className="flex-1"
                        data-testid="choose-from-library-btn"
                      >
                        <FolderOpen className="h-4 w-4 mr-2" />
                        From Library
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Supported formats: JPG, PNG, GIF, WebP. Max 10MB — compressed automatically.
                  </p>
                </div>

                {/* Mark as Answered — final sign-off after reviewing answer + images */}
                <div className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <Checkbox
                    id="has-answer"
                    checked={editFormData.has_answer}
                    onCheckedChange={(checked) => setEditFormData({ ...editFormData, has_answer: checked })}
                  />
                  <div className="flex-1">
                    <Label htmlFor="has-answer" className="cursor-pointer font-medium text-sm text-green-800 leading-none">
                      Mark as Answered
                    </Label>
                    <p className="text-xs text-green-700 mt-1">Check when satisfied with the answer, images, and video</p>
                  </div>
                </div>

              </TabsContent>

              {/* ──────────── METADATA TAB ──────────── */}
              <TabsContent value="metadata" className="space-y-4 mt-0">
                {/* Status Checkboxes Row — Decider & MARQ only (Answered is in Content tab) */}
                <div className="grid grid-cols-1 gap-3">
                  {/* Decider & MARQ Recommended Checkboxes */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center space-x-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <Checkbox
                        id="is-decider"
                        checked={editFormData.is_decider}
                        onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_decider: checked })}
                        className="mt-0"
                      />
                      <Label htmlFor="is-decider" className="cursor-pointer font-medium text-xs text-red-800 leading-none">
                        Decider Question
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                      <Checkbox
                        id="is-marq-recommended"
                        checked={editFormData.is_marq_recommended}
                        onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_marq_recommended: checked })}
                        className="mt-0"
                      />
                      <Label htmlFor="is-marq-recommended" className="cursor-pointer font-medium text-xs text-purple-800 leading-none">
                        MARQ Recommended
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Topics — multi-select tag editor (iter 105.2: uses
                    the shared <TopicPickerMulti> so grandchild+ topics
                    are selectable at their correct visual depth, with
                    a redundancy hint when a parent + descendant are
                    both tagged). Scoped to the editing question's
                    grade + function via `getFilteredTopics`. */}
                <div>
                  <Label className="text-sm font-medium">Topics</Label>
                  <div className="mt-1">
                    <TopicPickerMulti
                      topics={getFilteredTopics(editingQuestion)}
                      value={selectedTopicIds}
                      onChange={setSelectedTopicIds}
                      placeholder="Tag topics for this question…"
                      testIdPrefix="edit-question-tags"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Topics for {editingQuestion?.grade} • {functions.find(f => f.id === editingQuestion?.function_id)?.name}
                  </p>
                </div>

                {/* Exam Month + Surveyor Assignment removed — those values
                    are now derived from the sittings array below by the
                    backend (recompute_from_history). Editing them here was
                    a footgun: the sittings section always wins on save.
                    If the original submitter typed free-text surveyor names
                    (`surveyor_internal_other`/`surveyor_external_other`),
                    they surface on the review-submissions queue where they
                    matter — not on the post-approval edit modal. */}

                {/* Sittings Editor — full CRUD over the asked_by_history array.
                    Total `asked` count + top-level surveyor/exam_month get
                    recomputed BY THE BACKEND from this list on save. */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                      <History className="w-4 h-4 text-blue-700" />
                      Sittings — Full History
                    </h3>
                  </div>
                  <SittingsEditor
                    value={editingSittings}
                    onChange={setEditingSittings}
                    surveyors={surveyors}
                    defaultMmdLocation={editingQuestion?.mmd_location}
                    convertTextToMonthInput={convertTextToMonthInput}
                    convertMonthInputToText={convertMonthInputToText}
                    testIdPrefix="edit-sitting"
                  />
                </div>

                {/* Metadata */}
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-1 text-xs">
                  <p className="font-semibold text-indigo-900 mb-2">📋 Metadata</p>
                  {editingQuestion.approved_by_admin_id && (
                    <p className="text-indigo-800">
                      <span className="font-medium">Approved:</span> {approvedByIndos || editingQuestion.approved_by_admin_id} 
                      {editingQuestion.approved_at && (
                        <span> ({new Date(editingQuestion.approved_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata'
                        })})</span>
                      )}
                    </p>
                  )}
                  {editingQuestion.updated_at && (
                    <p className="text-indigo-800">
                      <span className="font-medium">Modified:</span> {lastModifiedByIndos || editingQuestion.last_modified_by_admin_id || 'System'} 
                      {editingQuestion.updated_at && (
                        <span> ({new Date(editingQuestion.updated_at).toLocaleDateString('en-IN', { 
                          day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata'
                        })})</span>
                      )}
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
            
          {/* Reports Section - Full Width Below Two Columns - Collapsible */}
          {editingQuestion && (loadingReports || questionReports.length > 0) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowReportsSection(!showReportsSection)}
                  className="flex items-center gap-2 w-full text-left hover:bg-gray-50 rounded-lg p-2 -ml-2 transition-colors"
                >
                  {showReportsSection ? (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  )}
                  <Flag className="w-5 h-5 text-red-600" />
                  <h3 className="text-base font-semibold text-gray-900">
                    User Reports
                    {questionReports.length > 0 && (
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({questionReports.filter(r => r.status === 'pending' || r.status === 'under_review').length} pending)
                      </span>
                    )}
                  </h3>
                </button>
                
                {showReportsSection && (
                  <>
                    {loadingReports ? (
                      <div className="text-center py-4 text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Loading reports...
                      </div>
                    ) : questionReports.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No reports for this question
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto mt-3">
                    {questionReports.map((report) => {
                      const isExpanded = expandedReportIds.has(report.id);
                      const isActive = report.status === 'pending' || report.status === 'under_review';
                      
                      return (
                      <div 
                        key={report.id} 
                        className={`rounded-lg border ${
                          isActive
                            ? 'bg-yellow-50 border-yellow-200'
                            : report.status === 'resolved'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        {/* Collapsible Report Header - Always Visible */}
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedReportIds(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(report.id)) {
                                newSet.delete(report.id);
                              } else {
                                newSet.add(report.id);
                              }
                              return newSet;
                            });
                          }}
                          className="w-full p-3 flex items-center justify-between gap-3 hover:bg-black/5 rounded-lg transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-wrap flex-1">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            )}
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <User className="w-4 h-4" />
                              <span className="font-medium">{report.reporter_indos}</span>
                            </div>
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              {REPORT_TYPE_LABELS[report.report_type] || report.report_type}
                            </Badge>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                report.status === 'pending' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                report.status === 'under_review' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                report.status === 'resolved' ? 'bg-green-100 text-green-800 border-green-300' :
                                'bg-gray-100 text-gray-800 border-gray-300'
                              }`}
                            >
                              {report.status === 'pending' ? 'Pending' :
                               report.status === 'under_review' ? 'Under Review' :
                               report.status === 'resolved' ? 'Resolved' : 'Dismissed'}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(report.created_at).toLocaleDateString('en-IN', { 
                                day: 'numeric', month: 'short', year: 'numeric'
                              })}
                            </span>
                          </div>
                        </button>
                        
                        {/* Collapsible Report Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-1 border-t border-gray-200/50">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-2">
                                {/* Reporter Full Info */}
                                {report.reporter_name && (
                                  <p className="text-sm text-gray-600">
                                    Reporter: <span className="font-medium">{report.reporter_name}</span>
                                  </p>
                                )}
                                
                                {/* Description */}
                                <div className="bg-white p-3 rounded border text-sm">
                                  <p className="font-medium text-gray-700 mb-1">Issue Description:</p>
                                  <p className="text-gray-600">{report.description}</p>
                                </div>
                                
                                {/* Suggested Correction */}
                                {report.suggested_correction && (
                                  <div className="bg-blue-50 p-3 rounded border border-blue-200 text-sm">
                                    <p className="font-medium text-blue-700 mb-1">💡 Suggested Correction:</p>
                                    <p className="text-blue-600">{report.suggested_correction}</p>
                                  </div>
                                )}
                                
                                {/* Admin Notes (if already resolved/dismissed) */}
                                {report.admin_notes && (
                                  <div className="bg-purple-50 p-3 rounded border border-purple-200 text-sm">
                                    <p className="font-medium text-purple-700 mb-1">Admin Notes:</p>
                                    <p className="text-purple-600">{report.admin_notes}</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Action Buttons - Only for pending/under_review */}
                              {isActive && (
                                <div className="flex flex-col gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleResolveReport(report.id);
                                    }}
                                    disabled={resolvingReport === report.id}
                                    className="text-green-600 border-green-300 hover:bg-green-50"
                                  >
                                    {resolvingReport === report.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        Resolve
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDismissReport(report.id);
                                    }}
                                    disabled={resolvingReport === report.id}
                                    className="text-gray-600 border-gray-300 hover:bg-gray-50"
                                  >
                                    {resolvingReport === report.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Dismiss
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                    )}
                  </>
                )}
              </div>
            )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            {/* Show "Update & Resolve" button if there are pending reports */}
            {questionReports.filter(r => r.status === 'pending' || r.status === 'under_review').length > 0 ? (
              <>
                <Button onClick={handleUpdateQuestion} variant="outline" className="border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10">
                  Update Only
                </Button>
                <Button onClick={handleUpdateAndResolve} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Update & Resolve Reports
                </Button>
              </>
            ) : (
              <Button onClick={handleUpdateQuestion} className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/90">
                Update Question
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-[#10B981]" />
              Export Oral Questions
            </DialogTitle>
            <DialogDescription>
              Select filters and format. Image URLs will be included as separate columns.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Export Format */}
            <div>
              <Label className="text-sm font-medium">Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (.csv)</SelectItem>
                  <SelectItem value="excel">Excel (.xlsx)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Grade Filter */}
            <div>
              <Label className="text-sm font-medium">Grade</Label>
              <Select 
                value={exportFilters.grade} 
                onValueChange={(value) => setExportFilters({...exportFilters, grade: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Grades</SelectItem>
                  {grades.map((grade) => (
                    <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* MMD Location Filter */}
            <div>
              <Label className="text-sm font-medium">MMD Location</Label>
              <Select 
                value={exportFilters.mmd_location} 
                onValueChange={(value) => setExportFilters({...exportFilters, mmd_location: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {mmdLocations.map((mmd) => (
                    <SelectItem key={mmd} value={mmd}>{mmd}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Function Filter */}
            <div>
              <Label className="text-sm font-medium">Function</Label>
              <Select 
                value={exportFilters.function_id} 
                onValueChange={(value) => setExportFilters({...exportFilters, function_id: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Functions</SelectItem>
                  {functions.map((func) => (
                    <SelectItem key={func.id} value={func.id}>{func.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Surveyor Filter */}
            <div>
              <Label className="text-sm font-medium">Surveyor</Label>
              <Select 
                value={exportFilters.surveyor_internal_id} 
                onValueChange={(value) => setExportFilters({...exportFilters, surveyor_internal_id: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Surveyors</SelectItem>
                  {surveyors.map((surveyor) => (
                    <SelectItem key={surveyor.id} value={surveyor.id}>{surveyor.name} ({surveyor.type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Export Count Info */}
            {exportCount > 0 && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>{exportCount.toLocaleString()}</strong> questions will be exported based on current filters
                </p>
              </div>
            )}

            {/* Image URL Info */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700 font-medium">Image URLs Included:</p>
              <p className="text-xs text-gray-500 mt-1">
                Question Image 1-4, Answer Image 1-4, Additional Answer Images (if any)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseExportDialog} disabled={exporting}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport} 
              className="bg-[#10B981] hover:bg-[#10B981]/90"
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Picker Modal */}
      <ImagePickerModal
        isOpen={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onSelect={handleAddFromLibrary}
        maxSelections={4 - editFormData.answer_images.length}
        currentImages={editFormData.answer_images}
      />

      <PictureManagerUploadDialog
        open={pmUploadOpen}
        onOpenChange={setPmUploadOpen}
        onImageAdded={handlePMImageAdded}
      />

      {/* History Modal - Shows all surveyors who asked the question */}
      <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, question: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Question History
            </DialogTitle>
            <DialogDescription>
              All surveyors who asked this question
            </DialogDescription>
          </DialogHeader>
          {historyModal.question && (
            <div className="mt-4 max-h-[60vh] overflow-y-auto pr-2 space-y-3">
              {getFullHistory(historyModal.question).map((entry, idx) => (
                <div 
                  key={idx} 
                  className="p-3 rounded-lg border bg-gray-50 border-gray-200"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 flex items-center gap-1.5">
                      <User className="w-4 h-4 text-gray-500" />
                      {entry.surveyor_name}
                      {entry.count > 1 && (
                        <Badge className="bg-orange-500 text-white text-xs ml-1">
                          x{entry.count}
                        </Badge>
                      )}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {entry.surveyor_type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    {entry.exam_month && formatExamMonth(entry.exam_month) && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatExamMonth(entry.exam_month)}
                      </span>
                    )}
                    {entry.mmd_location && (
                      <span className="text-gray-500">
                        {entry.mmd_location}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Research Sheet */}
      <Sheet open={showResearch} onOpenChange={setShowResearch}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px] flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-violet-700">
              <BookOpen className="w-5 h-5" />
              Research
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 mt-4 overflow-hidden">
            <ResearchPanel questionText={editFormData.question_text} currentGrade={editFormData.grade} />
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── Sticky Bulk Action Bar ─────────────────────────────────
          The actual bar renders inline just below the filters card
          (see above, near the results list) using `sticky top-16`, so
          it sits in normal flow first and only sticks to the top when
          scrolled past. No fallback bar needed here. */}
    </Layout>
  );
};

export default AdminQuestionsPage;
